import {
  FORMAT_CONFIG,
  type FormatSurface,
  type PrecisionRule
} from "@/constants/format";
import { capitalizeFirst } from "@/helpers/string";
import {
  DataSchemaType,
  DatasetDataRowValue,
  DatasetSchemaLabel
} from "@/types/dataset";

import { formatFixed, formatNumber } from "./number";

/**
 * Labels that name a numeric subtype. A column may carry the subtype in
 * `data_label` while `data_type` says only `float`, so the label has to be
 * consulted first — see `resolveEffectiveType`.
 */
const LABEL_TO_TYPE: Partial<Record<DatasetSchemaLabel, DataSchemaType>> = {
  [DatasetSchemaLabel.LATITUDE]: DataSchemaType.LATITUDE,
  [DatasetSchemaLabel.LONGITUDE]: DataSchemaType.LONGITUDE,
  [DatasetSchemaLabel.PERCENTAGE]: DataSchemaType.PERCENTAGE,
  [DatasetSchemaLabel.RATIO]: DataSchemaType.RATIO
};

/**
 * Schema types that name a numeric subtype directly, rather than through the
 * label. `DATA_TYPE_VALUES` produces them whenever the backend sends
 * `TYPE_LATITUDE` and friends, so real columns do arrive this way.
 *
 * They render as raw text, because that is what the previous formatter did:
 * its switch handled only `int`, `float` and `bool`, so a column already typed
 * as a subtype fell through untouched. Formatting them is the better
 * behaviour and is coming, but it is a visible change and does not belong in a
 * refactor.
 */
const SUBTYPE_AS_TYPE = new Set<DataSchemaType>([
  DataSchemaType.LATITUDE,
  DataSchemaType.LONGITUDE,
  DataSchemaType.PERCENTAGE,
  DataSchemaType.RATIO
]);

/**
 * Picks the type that decides precision.
 *
 * A numeric subtype normally lives in `data_label` while `data_type` says only
 * `float` — a latitude column arrives as `data_type: float` + `data_label:
 * latitude`, and reading `data_type` alone would render it at 3 decimals
 * instead of 6. The label is therefore consulted, but only for a float: the
 * previous formatter read it inside its `float` branch alone, so a label on an
 * `int` or `bool` column never changed anything.
 *
 * When neither names a subtype the value is indistinguishable from a plain
 * number and is formatted as one. Column names are deliberately not used as a
 * hint — a wrong guess is worse than a consistent miss.
 */
export function resolveEffectiveType(
  type: DataSchemaType,
  label?: DatasetSchemaLabel
): DataSchemaType {
  if (type !== DataSchemaType.FLOAT) return type;
  return (label && LABEL_TO_TYPE[label]) || type;
}

/** Merges default, surface and type rules. Type wins, then surface. */
function resolveRule(
  type: DataSchemaType,
  surface: FormatSurface
): Required<PrecisionRule> {
  const merged = {
    fractionDigits: FORMAT_CONFIG.fractionDigits,
    minFractionDigits: FORMAT_CONFIG.minFractionDigits,
    roundUp: FORMAT_CONFIG.roundUp,
    ...FORMAT_CONFIG.surfaces[surface],
    ...FORMAT_CONFIG.types[type]
  };

  return {
    fractionDigits: merged.fractionDigits,
    // Keep minFractionDigits within the maximum to avoid an Intl RangeError.
    minFractionDigits: Math.min(
      merged.minFractionDigits,
      merged.fractionDigits
    ),
    roundUp: merged.roundUp
  };
}

/** Renders a number with grouping separators at the given precision. */
function renderNumber(value: number, rule: Required<PrecisionRule>): string {
  return String(
    formatNumber(value, rule.fractionDigits === 0 ? "integer" : "decimal", {
      locale: FORMAT_CONFIG.locale,
      minimumFractionDigits: rule.minFractionDigits,
      maximumFractionDigits: rule.fractionDigits,
      isRoundUp: rule.roundUp
    })
  );
}

/**
 * Renders one `DataSchemaType`. Listed as a `Record` rather than a `switch` so
 * that adding a member to the enum without handling it is a compile error, and
 * so the pass-through types read as a deliberate choice rather than a default
 * branch nobody noticed.
 */
type Formatter = (value: string, rule: Required<PrecisionRule>) => string;

const asIs: Formatter = (value) => value;

// A value that will not parse renders as "NaN", which is what the dataset
// table has always shown for a malformed numeric cell.
const asNumber: Formatter = (value, rule) => renderNumber(Number(value), rule);

const asPercentage: Formatter = (value, rule) =>
  `${renderNumber(Number(value), rule)}%`;

const asBoolean: Formatter = (value) => capitalizeFirst(value);

const FORMATTERS: Record<DataSchemaType, Formatter> = {
  [DataSchemaType.INT]: asNumber,
  [DataSchemaType.FLOAT]: asNumber,
  [DataSchemaType.LATITUDE]: asNumber,
  [DataSchemaType.LONGITUDE]: asNumber,
  [DataSchemaType.RATIO]: asNumber,
  [DataSchemaType.PERCENTAGE]: asPercentage,
  [DataSchemaType.BOOL]: asBoolean,

  // Dates keep their own dispatcher (`translateToUserTimezone`); formatting
  // them here would mean two places deciding the same thing.
  [DataSchemaType.DATE]: asIs,
  [DataSchemaType.TIME]: asIs,
  [DataSchemaType.DATE_TIME]: asIs,

  // Text-shaped types: nothing to format.
  [DataSchemaType.ID]: asIs,
  [DataSchemaType.TEXT]: asIs,
  [DataSchemaType.TEXT2]: asIs,
  [DataSchemaType.TITLE]: asIs,
  [DataSchemaType.ADDRESS]: asIs,
  [DataSchemaType.JSON]: asIs,
  [DataSchemaType.GEOMETRY]: asIs,
  [DataSchemaType.PHOTOS]: asIs,
  [DataSchemaType.LINKS]: asIs
};

/**
 * Formats a value that carries a dataset schema type.
 *
 * This is the entry point for anything rendered from dataset data. Precision
 * comes from `FORMAT_CONFIG`, so call sites do not choose their own.
 */
export function formatValue({
  type,
  label,
  value,
  surface = "table",
  fractionDigits
}: {
  type: DataSchemaType;
  /** Schema label. Wins over `type` when it names a numeric subtype. */
  label?: DatasetSchemaLabel;
  /** A dataset cell value. Primitives only — see `DatasetDataRowValue`. */
  value: DatasetDataRowValue;
  surface?: FormatSurface;
  /** Escape hatch for a one-off precision; prefer a rule in FORMAT_CONFIG. */
  fractionDigits?: number;
}): string {
  if (value === null || value === undefined) return "";

  if (SUBTYPE_AS_TYPE.has(type)) return String(value);

  const effectiveType = resolveEffectiveType(type, label);
  const rule = resolveRule(effectiveType, surface);

  // The record is exhaustive at compile time, but the backend can still send a
  // type this build has never heard of. Show it rather than throw.
  const format = FORMATTERS[effectiveType] ?? asIs;

  return format(
    String(value),
    fractionDigits === undefined
      ? rule
      : { ...rule, fractionDigits, minFractionDigits: 0 }
  );
}

/**
 * Formats a plain measure. Callers that hold a number with no schema type
 * attached — a score, a break boundary — go through here.
 */
export function formatMeasure(
  value: number | string | null | undefined,
  surface: FormatSurface = "chart"
): string {
  return formatValue({ type: DataSchemaType.FLOAT, value, surface });
}

/**
 * Formats a chart share value using the configured decimal places and
 * `toFixed` rounding behavior.
 */
export function formatSharePercent(percent: number): string {
  return formatFixed(percent, FORMAT_CONFIG.chartShare);
}

export function formatStorageFromKB(kb: number): string {
  if (!Number.isFinite(kb)) return "0 KB";

  const absKB = Math.abs(kb);
  const KB_IN_MB = 1024;
  const KB_IN_GB = 1024 * 1024;

  const trimTrailingZeros = (value: number): string =>
    Number.isInteger(value)
      ? value.toString()
      : Number(value.toFixed(2)).toString();

  if (absKB >= KB_IN_GB) {
    const gb = absKB / KB_IN_GB;
    return `${trimTrailingZeros(gb)} GB`;
  } else if (absKB >= KB_IN_MB) {
    const mb = absKB / KB_IN_MB;
    return `${trimTrailingZeros(mb)} MB`;
  }

  return `${trimTrailingZeros(absKB)} KB`;
}
